import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { Building2, Users } from 'lucide-react';
import { Card } from '@/components';
import { JoinCompanyDialog } from './JoinCompanyDialog';

interface CompanySelectorProps {
  onJoinSuccess?: () => void;
}

export function CompanySelector({ onJoinSuccess }: CompanySelectorProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isJoinDialogOpen, setIsJoinDialogOpen] = useState(false);

  const handleCreateCompany = useCallback(() => {
    navigate({ to: '/addMyCompany' });
  }, [navigate]);

  const handleJoinCompany = useCallback(() => {
    setIsJoinDialogOpen(true);
  }, []);

  const handleJoinDialogClose = useCallback(() => {
    setIsJoinDialogOpen(false);
  }, []);

  const handleJoinSuccess = useCallback(() => {
    setIsJoinDialogOpen(false);
    onJoinSuccess?.();
  }, [onJoinSuccess]);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card
          padding="none"
          className="cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-primary/50 hover:scale-[1.02] group"
        >
          <button
            type="button"
            onClick={handleCreateCompany}
            className="w-full h-full p-6 text-left"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
              <h4 className="font-semibold text-onsurface mb-2">
                {t('companySelector.createCompany.title')}
              </h4>
              <p className="text-sm text-muted">
                {t('companySelector.createCompany.description')}
              </p>
            </div>
          </button>
        </Card>

        <Card
          padding="none"
          className="cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-primary/50 hover:scale-[1.02] group"
        >
          <button
            type="button"
            onClick={handleJoinCompany}
            className="w-full h-full p-6 text-left"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <h4 className="font-semibold text-onsurface mb-2">
                {t('companySelector.joinCompany.title')}
              </h4>
              <p className="text-sm text-muted">
                {t('companySelector.joinCompany.description')}
              </p>
            </div>
          </button>
        </Card>
      </div>

      <JoinCompanyDialog
        isOpen={isJoinDialogOpen}
        onClose={handleJoinDialogClose}
        onSuccess={handleJoinSuccess}
      />
    </>
  );
}
